import {
  ACTIVE_ORG_STORAGE_KEY,
  can,
  canOn,
  createSessionStore,
  entitlementLimit,
  hasEntitlement,
  selectActiveMembership,
  selectCsrfToken,
  selectEntitlements,
  selectPermissions,
  selectSessionRemainingMs
} from "./chunk-YXNSSCIE.js";

// src/auth/urls.ts
import { isSafeReturnTo } from "@batiflow/contracts";
function safeReturnTo(path) {
  return path && isSafeReturnTo(path) ? path : "/";
}
function loginUrl(returnTo, options = {}) {
  const parts = [`returnTo=${encodeURIComponent(safeReturnTo(returnTo))}`];
  if (options.enrol) parts.push(`enrol=${options.enrol}`);
  if (options.fresh) parts.push("fresh=1");
  if (options.locale) parts.push(`locale=${options.locale}`);
  return `/api/v1/auth/login?${parts.join("&")}`;
}
var stepUpUrl = (returnTo) => `/api/v1/auth/step-up?returnTo=${encodeURIComponent(safeReturnTo(returnTo))}`;

// src/auth/pending-action.ts
import { z } from "zod";
var PENDING_ACTION_KEY = "bf.pendingAction";
var PENDING_ACTION_TTL_MS = 10 * 60 * 1e3;
var PendingActionSchema = z.strictObject({
  v: z.literal(1),
  method: z.enum(["POST", "PUT", "PATCH", "DELETE"]),
  url: z.string().regex(/^\/api\/v1\/(?![/\\])[^\s\\]*$/).max(1e3),
  params: z.record(z.string(), z.string()).optional(),
  data: z.unknown().optional(),
  idempotencyKey: z.string().min(16).max(100),
  etag: z.string().max(200).optional(),
  createdAt: z.number().int()
});
var SECRET_FIELD = /^(rib|iban|bankDetails|password|passphrase|secret|token|accessToken|refreshToken|apiKey|privateKey|privateKeyPem|privateKeyEnc|otp|totp|cardNumber|cvc|hourlyCostMinor)$/i;
var PEM_BLOCK = "-----BEGIN ";
function holdsSecrets(value, depth = 0) {
  if (typeof value === "string") return value.includes(PEM_BLOCK);
  if (depth > 8 || value === null || typeof value !== "object") return false;
  if (Array.isArray(value)) return value.some((item) => holdsSecrets(item, depth + 1));
  return Object.entries(value).some(
    ([key, item]) => SECRET_FIELD.test(key) || holdsSecrets(item, depth + 1)
  );
}
function savePendingAction(storage, clock, request, newIdempotencyKey) {
  if (request.method === "GET" || request.method === "HEAD" || request.method === "OPTIONS")
    return false;
  if (holdsSecrets(request.data)) return false;
  const params = {};
  for (const [key, value] of Object.entries(request.params ?? {}))
    if (value !== null && value !== void 0 && !Array.isArray(value)) params[key] = String(value);
  const action = PendingActionSchema.safeParse({
    v: 1,
    method: request.method,
    url: request.url,
    ...Object.keys(params).length > 0 ? { params } : {},
    ...request.data === void 0 ? {} : { data: request.data },
    idempotencyKey: request.idempotencyKey ?? newIdempotencyKey(),
    ...request.etag ? { etag: request.etag } : {},
    createdAt: clock.now()
  });
  if (!action.success) return false;
  try {
    storage.setItem(PENDING_ACTION_KEY, JSON.stringify(action.data));
    return true;
  } catch {
    return false;
  }
}
function takePendingAction(storage, clock) {
  const raw = storage.getItem(PENDING_ACTION_KEY);
  storage.removeItem(PENDING_ACTION_KEY);
  if (!raw) return null;
  try {
    const parsed = PendingActionSchema.safeParse(JSON.parse(raw));
    if (!parsed.success || clock.now() - parsed.data.createdAt > PENDING_ACTION_TTL_MS) return null;
    return parsed.data;
  } catch {
    return null;
  }
}
function pendingActionRequest(action) {
  return {
    method: action.method,
    url: action.url,
    ...action.params ? { params: action.params } : {},
    ...action.data === void 0 ? {} : { data: action.data },
    idempotencyKey: action.idempotencyKey,
    ...action.etag ? { etag: action.etag } : {}
  };
}

// src/etag/etag-tracker.ts
function serializeKey(key) {
  return JSON.stringify(
    key,
    (_k, value) => value && typeof value === "object" && !Array.isArray(value) ? Object.fromEntries(
      Object.entries(value).sort(([a], [b]) => a.localeCompare(b))
    ) : value
  );
}
var ETagTracker = class {
  constructor(maxEntries = 2e3) {
    this.maxEntries = maxEntries;
  }
  maxEntries;
  etags = /* @__PURE__ */ new Map();
  remember(key, etag) {
    const k = serializeKey(key);
    if (!etag) {
      this.etags.delete(k);
      return;
    }
    this.etags.delete(k);
    this.etags.set(k, etag);
    if (this.etags.size > this.maxEntries) {
      const oldest = this.etags.keys().next().value;
      if (oldest !== void 0) this.etags.delete(oldest);
    }
  }
  get(key) {
    return this.etags.get(serializeKey(key));
  }
  forget(key) {
    this.etags.delete(serializeKey(key));
  }
  clear() {
    this.etags.clear();
  }
};

// src/http/api-error.ts
import {
  ProblemDetailsSchema
} from "@batiflow/contracts";
var ApiError = class extends Error {
  name = "ApiError";
  status;
  code;
  traceId;
  fieldErrors;
  problem;
  constructor(status, code, problem) {
    super(problem?.title ?? `HTTP ${String(status)}`);
    this.status = status;
    this.code = code;
    this.problem = problem;
    this.traceId = problem?.traceId;
    this.fieldErrors = problem?.errors ?? [];
  }
};
function fallbackCode(status) {
  if (status === 401) return "SESSION_EXPIRED";
  if (status === 403) return "FORBIDDEN";
  if (status === 404) return "NOT_FOUND";
  if (status === 429) return "RATE_LIMITED";
  if (status === 503 || status === 502 || status === 504) return "SERVICE_UNAVAILABLE";
  return status >= 500 ? "INTERNAL_ERROR" : "BAD_REQUEST";
}
function apiErrorFrom(status, contentType, bodyText) {
  if (contentType?.includes("json")) {
    try {
      const problem = ProblemDetailsSchema.safeParse(JSON.parse(bodyText));
      if (problem.success) return new ApiError(status, problem.data.code, problem.data);
    } catch {
    }
  }
  return new ApiError(status, fallbackCode(status));
}

// src/http/api-fetcher.ts
var UNSAFE = /* @__PURE__ */ new Set(["POST", "PUT", "PATCH", "DELETE"]);
function queryString(params) {
  if (!params) return "";
  const parts = [];
  for (const [key, raw] of Object.entries(params)) {
    if (raw === void 0 || raw === null) continue;
    const values = Array.isArray(raw) ? raw : [raw];
    for (const value of values)
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  }
  return parts.length > 0 ? `?${parts.join("&")}` : "";
}
function createApiFetcher(options) {
  const handlerFor = {
    SESSION_EXPIRED: options.handlers?.onUnauthorized,
    MFA_REQUIRED: options.handlers?.onMfaRequired,
    STEP_UP_REQUIRED: options.handlers?.onStepUpRequired,
    PLAN_LIMIT_REACHED: options.handlers?.onPlanLimitReached
  };
  return async (request) => {
    if (!/^\/(?![/\\])/.test(request.url) || request.url.includes("\\"))
      throw new Error("API paths must be same-origin absolute paths");
    const headers = {
      accept: "application/json",
      "accept-language": options.getLocale(),
      "x-request-id": options.randomId()
    };
    for (const [name, value] of Object.entries(request.headers ?? {}))
      headers[name.toLowerCase()] = value;
    if (UNSAFE.has(request.method)) {
      const csrf = options.getCsrfToken();
      if (csrf) headers["x-csrf-token"] = csrf;
    }
    if (request.etag) headers["if-match"] = request.etag;
    if (request.idempotencyKey) headers["idempotency-key"] = request.idempotencyKey;
    let body;
    if (request.data !== void 0) {
      headers["content-type"] = "application/json";
      body = JSON.stringify(request.data);
    }
    const response = await options.fetch(
      `${options.baseUrl ?? ""}${request.url}${queryString(request.params)}`,
      {
        method: request.method,
        headers,
        credentials: "include",
        ...body === void 0 ? {} : { body },
        ...request.signal === void 0 ? {} : { signal: request.signal }
      }
    );
    const text = await response.text();
    if (response.status >= 400) {
      const error = apiErrorFrom(response.status, response.headers.get("content-type"), text);
      if (!request.skipHandlers) handlerFor[error.code]?.(error, request);
      throw error;
    }
    return {
      status: response.status,
      data: text ? JSON.parse(text) : void 0,
      etag: response.headers.get("etag"),
      requestId: response.headers.get("x-request-id")
    };
  };
}

// src/ids/uuidv7.ts
var hex = (bytes) => Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
function uuidv7(clock, random) {
  const bytes = new Uint8Array(16);
  const ms = clock.now();
  if (!Number.isSafeInteger(ms) || ms < 0) throw new RangeError("clock returned an invalid time");
  let t = ms;
  for (let i = 5; i >= 0; i--) {
    bytes[i] = t % 256;
    t = Math.floor(t / 256);
  }
  const rand = random(10);
  if (rand.length !== 10) throw new RangeError("random source returned the wrong length");
  bytes.set(rand, 6);
  bytes[6] = 112 | (bytes[6] ?? 0) & 15;
  bytes[8] = 128 | (bytes[8] ?? 0) & 63;
  const h = hex(bytes);
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}
function createIdempotencyKeyFactory(clock, random) {
  return () => uuidv7(clock, random);
}

// src/ports/index.ts
function memoryStorage() {
  const data = /* @__PURE__ */ new Map();
  return {
    getItem: (key) => data.get(key) ?? null,
    setItem: (key, value) => {
      data.set(key, value);
    },
    removeItem: (key) => {
      data.delete(key);
    }
  };
}
var systemClock = { now: () => Date.now() };

// src/realtime/realtime-client.ts
import {
  RealtimeEnvelopeSchema,
  RealtimeRoomSchema,
  clientToServerEvents,
  serverToClientEvents
} from "@batiflow/contracts";
import { io } from "socket.io-client";
var REALTIME_PATH = "/api/rt";
var RealtimeClient = class {
  constructor(options = {}) {
    this.options = options;
    const factory = options.socketFactory ?? io;
    this.socket = factory(options.url ?? "", {
      path: REALTIME_PATH,
      autoConnect: false,
      withCredentials: true,
      transports: ["websocket"],
      reconnection: true,
      reconnectionDelay: 1e3,
      reconnectionDelayMax: 3e4,
      randomizationFactor: 0.5
    });
    this.socket.on("connect", () => {
      this.onConnect();
    });
    this.socket.on("disconnect", () => {
      this.options.onConnectionChange?.(false);
    });
    this.socket.onAny((event, payload) => {
      this.dispatch(event, payload);
    });
  }
  options;
  socket;
  rooms = /* @__PURE__ */ new Map();
  handlers = /* @__PURE__ */ new Map();
  seen = [];
  seenSet = /* @__PURE__ */ new Set();
  everConnected = false;
  get connected() {
    return this.socket.connected;
  }
  /** Opens the connection (call once the session is ready). */
  connect() {
    this.socket.connect();
  }
  /** Closes the connection on purpose; a later `connect()` is a fresh start, not a reconnection. */
  disconnect() {
    this.everConnected = false;
    this.socket.disconnect();
  }
  /** Subscribes a room; nested subscriptions to the same room are reference-counted. */
  subscribe(room) {
    RealtimeRoomSchema.parse(room);
    const count = this.rooms.get(room) ?? 0;
    this.rooms.set(room, count + 1);
    if (count === 0 && this.socket.connected) this.socket.emit("subscribe", { room });
  }
  unsubscribe(room) {
    const count = this.rooms.get(room);
    if (count === void 0) return;
    if (count > 1) {
      this.rooms.set(room, count - 1);
      return;
    }
    this.rooms.delete(room);
    if (this.socket.connected) this.socket.emit("unsubscribe", { room });
  }
  /**
   * Sends a client event (`typing`, `presence.set`), validated by the contracts registry. Returns
   * false (and sends nothing) while the connection is closed: these signals are never queued.
   */
  send(event, payload) {
    const data = clientToServerEvents[event].parse(payload);
    if (!this.socket.connected) return false;
    this.socket.emit(event, data);
    return true;
  }
  get subscribedRooms() {
    return [...this.rooms.keys()];
  }
  /** Listens to a server event; returns the function that removes the listener. */
  on(type, handler) {
    const set = this.handlers.get(type) ?? /* @__PURE__ */ new Set();
    const listener = handler;
    set.add(listener);
    this.handlers.set(type, set);
    return () => {
      set.delete(listener);
    };
  }
  onConnect() {
    const rooms = this.subscribedRooms;
    for (const room of rooms) this.socket.emit("subscribe", { room });
    this.options.onConnectionChange?.(true);
    if (this.everConnected) this.options.onReconnect?.(rooms);
    this.everConnected = true;
  }
  dispatch(event, payload) {
    const listeners = this.handlers.get(event);
    if (!listeners || listeners.size === 0) return;
    const envelope = RealtimeEnvelopeSchema.safeParse(payload);
    if (!envelope.success || envelope.data.type !== event) return;
    const schema = serverToClientEvents[event];
    const data = schema ? schema.safeParse(envelope.data.data) : { success: true, data: envelope.data.data };
    if (!data.success) return;
    if (this.seenSet.has(envelope.data.id)) return;
    this.remember(envelope.data.id);
    const message = { ...envelope.data, data: data.data };
    for (const listener of listeners) listener(message);
  }
  remember(id) {
    this.seen.push(id);
    this.seenSet.add(id);
    if (this.seen.length > (this.options.dedupeSize ?? 1e3)) {
      const oldest = this.seen.shift();
      if (oldest !== void 0) this.seenSet.delete(oldest);
    }
  }
};

// src/upload/upload-helper.ts
var uploadRatio = (p) => p.bytesTotal > 0 ? Math.min(1, p.bytesSent / p.bytesTotal) : 0;
export {
  ACTIVE_ORG_STORAGE_KEY,
  ApiError,
  ETagTracker,
  PENDING_ACTION_KEY,
  PENDING_ACTION_TTL_MS,
  REALTIME_PATH,
  RealtimeClient,
  apiErrorFrom,
  can,
  canOn,
  createApiFetcher,
  createIdempotencyKeyFactory,
  createSessionStore,
  entitlementLimit,
  hasEntitlement,
  holdsSecrets,
  loginUrl,
  memoryStorage,
  pendingActionRequest,
  safeReturnTo,
  savePendingAction,
  selectActiveMembership,
  selectCsrfToken,
  selectEntitlements,
  selectPermissions,
  selectSessionRemainingMs,
  serializeKey,
  stepUpUrl,
  systemClock,
  takePendingAction,
  uploadRatio,
  uuidv7
};
//# sourceMappingURL=index.js.map